import { getJson, sendJson } from '@/lib/api';

/**
 * The inquiry workroom: BCE's notes, tasks and decisions on an event request
 * or a large-event inquiry, and the decision that promotes a request to an
 * event. Internal only; the client sees the outcome and note, never these.
 */

export type InquirySubjectType = 'booking_request' | 'large_event_inquiry';
export interface InquirySubject {
  readonly type: InquirySubjectType;
  readonly id: string;
}

export const ENTRY_KIND = { note: 'note', task: 'task', decision: 'decision' } as const;
export type EntryKind = (typeof ENTRY_KIND)[keyof typeof ENTRY_KIND];

export interface InquiryEntry {
  readonly id: string;
  readonly kind: EntryKind;
  readonly body: string;
  readonly why: string | null;
  readonly done: boolean;
  readonly doneAt: string | null;
  readonly dueAt: string | null;
  readonly authorName: string;
  readonly createdAt: string;
}

export interface NewEntry {
  readonly kind: EntryKind;
  readonly body: string;
  readonly why?: string;
  readonly dueAt?: string | null;
}

export interface BookingInquiry {
  /** A client's request to cancel this confirmed event, waiting on BCE; null when there is none. */
  readonly cancellationRequest?: { readonly at: string; readonly byName: string | null; readonly reason: string } | null;
  readonly id: string;
  readonly reference: string;
  readonly status: string;
  readonly programName: string;
  readonly preferredDate: string | null;
  readonly alternateDate: string | null;
  readonly city: string | null;
  readonly state: string | null;
  readonly venueName: string | null;
  readonly venueStatus: string | null;
  readonly deliveryMethod: string | null;
  readonly estimatedAttendees: number;
  readonly estimatedCredits: number;
  readonly objectives: string | null;
  readonly notes: string | null;
  readonly specialRequirements: string | null;
  readonly brandingRequests: string | null;
  readonly csrInterests: string | null;
  readonly accessibilityNeeds: string | null;
  readonly onSiteContact: string | null;
  readonly venueContact: string | null;
  readonly submittedAt: string | null;
  readonly requester: { readonly name: string; readonly email: string } | null;
  readonly teamName: string | null;
  readonly feasibility: {
    readonly outcome: string | null;
    readonly clientNote: string | null;
    readonly reviewedByName: string | null;
    readonly reviewedAt: string | null;
  };
  readonly outcomes: readonly { readonly value: string; readonly label: string }[];
}

export const BOOKING_DECISION = {
  promote: 'PROMOTE',
  needsInformation: 'NEEDS_INFORMATION',
  hold: 'HOLD',
  decline: 'DECLINE',
} as const;
export type BookingDecision = (typeof BOOKING_DECISION)[keyof typeof BOOKING_DECISION];

const entriesPath = (organizationId: string, subject: InquirySubject): string =>
  `/organizations/${organizationId}/inquiries/${subject.type}/${subject.id}/entries`;

export const listEntries = (organizationId: string, subject: InquirySubject, signal?: AbortSignal): Promise<InquiryEntry[]> =>
  getJson<InquiryEntry[]>(entriesPath(organizationId, subject), undefined, signal);

export const addEntry = (organizationId: string, subject: InquirySubject, entry: NewEntry): Promise<InquiryEntry> =>
  sendJson<InquiryEntry>('post', entriesPath(organizationId, subject), entry);

export const updateEntry = (organizationId: string, entryId: string, patch: { done?: boolean; body?: string; why?: string | null; dueAt?: string | null }): Promise<InquiryEntry> =>
  sendJson<InquiryEntry>('patch', `/organizations/${organizationId}/inquiry-entries/${entryId}`, patch);

export const removeEntry = (organizationId: string, entryId: string): Promise<null> =>
  sendJson<null>('delete', `/organizations/${organizationId}/inquiry-entries/${entryId}`);

export const readBookingInquiry = (organizationId: string, bookingId: string, signal?: AbortSignal): Promise<BookingInquiry> =>
  getJson<BookingInquiry>(`/organizations/${organizationId}/bookings/${bookingId}`, undefined, signal);

export const decideBooking = (
  organizationId: string,
  bookingId: string,
  input: { decision: BookingDecision; outcome?: string; clientNote?: string },
): Promise<{ id: string; status: string }> =>
  sendJson('post', `/organizations/${organizationId}/bookings/${bookingId}/decision`, input);

/** How BCE settles a client's request to cancel a confirmed event. */
export const CANCELLATION_DECISION = { cancel: 'CANCEL', keep: 'KEEP' } as const;
export type CancellationDecision = (typeof CANCELLATION_DECISION)[keyof typeof CANCELLATION_DECISION];

export const settleCancellation = (
  organizationId: string,
  bookingId: string,
  input: { decision: CancellationDecision; creditsToReturn?: number; note?: string },
): Promise<{ cancelled: boolean; creditsReturned: number }> =>
  sendJson('post', `/organizations/${organizationId}/bookings/${bookingId}/cancellation`, input);
